import type { LocalStorageService } from "@packages/common";
import type {
  FormData,
  GeolocationCookieService,
  GeolocationData,
  LeadFormDataService,
} from "../../cookies";
import type { User } from "../../user";
import { UserLoginStatus } from "../dtos";

/**
 * Abstract class for transforming user-related data.
 */
export abstract class UserTransformer {
  private static readonly LEAD_STORAGE_KEY = "userPiiLead";
  private static readonly LOCATION_STORAGE_KEY = "userPiiLocation";

  /**
   * Retrieves or generates the user's Personally Identifiable Information (PII) lead data.
   *
   * @param leadFormData - The lead form data service for retrieving form data.
   * @param storage - The local storage service for managing stored user data.
   * @returns A partial `FormData` object containing the user's PII lead data.
   */
  static userPiiLead(
    leadFormData: LeadFormDataService,
    storage: LocalStorageService
  ): Partial<FormData> {
    const defaultValues = UserTransformer.getDefaultPiiLead();

    const storedData = storage.getItem(
      UserTransformer.LEAD_STORAGE_KEY
    ) as Partial<FormData>;

    const formData = leadFormData.get();

    const { email, name, formattedPhoneNumber } = formData;

    let userData = storedData || defaultValues;

    if (email || name || formattedPhoneNumber) {
      userData = {
        email,
        name,
        phoneNumber: formattedPhoneNumber,
      };
      storage.setItem(
        UserTransformer.LEAD_STORAGE_KEY,
        JSON.stringify(userData)
      );
    }

    return userData;
  }

  static userPiiLocation(
    geolocation: GeolocationCookieService,
    storage: LocalStorageService
  ) {
    const defaultValues = UserTransformer.getDefaultPiiLocation();

    const locationData: GeolocationData | null = storage.getItem(
      UserTransformer.LOCATION_STORAGE_KEY
    );
    const geolocationCookieData = geolocation.get();

    const { state, city } = geolocationCookieData;

    let userData = locationData || defaultValues;

    if (state || city) {
      userData = {
        state,
        city,
      };
      storage.setItem(
        UserTransformer.LOCATION_STORAGE_KEY,
        JSON.stringify(userData)
      );
    }

    return userData;
  }

  /**
   * Transforms the user data into a structured format for use in other services.
   *
   * @param user - The current user object, or `null` if not logged in.
   * @param leadFormData - The lead form data service for retrieving form data.
   * @param storage - The local storage service for managing stored user data.
   * @returns A transformed user object.
   */
  static transform(
    user: User | null,
    leadFormData: LeadFormDataService,
    storage: LocalStorageService,
    geolocation: GeolocationCookieService
  ) {
    return {
      pii: {
        lead: UserTransformer.userPiiLead(leadFormData, storage),
        location: {
          optin: UserTransformer.userPiiLocation(geolocation, storage),
        },
      },
      loginStatus: user ? UserLoginStatus.LOGGED : UserLoginStatus.LOGGED_OUT,
    };
  }

  /**
   * Provides the default values for the user's PII lead data.
   *
   * @returns A partial `FormData` object with default values.
   */
  private static getDefaultPiiLead(): Partial<FormData> {
    return {
      email: undefined,
      name: undefined,
      phoneNumber: undefined,
    };
  }

  /**
   * Provides the default values for the user's PII geolocation data.
   *
   * @returns A partial `GeolocationData` object with default values.
   */
  private static getDefaultPiiLocation(): Partial<GeolocationData> {
    return {
      state: undefined,
      city: undefined,
    };
  }
}
